import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import styles from './Menu.module.scss';

export interface MenuPropsI {
  children: React.ReactNode;
  ref?: React.LegacyRef<HTMLUListElement>;
  orientation: 'horizontal' | 'vertical';
  justify?:
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'flex-start'
    | 'flex-end';
  height?: string;
}

export interface MenuItemPropsI {
  children?: React.ReactNode;
  onClick?: () => void;
  isactive?: boolean;
  variant: 'light' | 'primary' | 'default' | 'dark' | 'gray';
  ref?: React.LegacyRef<HTMLUListElement>;
  link?: string;
  outsideLink?: boolean;
  newWindow?: boolean;
  hide?: boolean;
}

export interface MenuMultiChoiceOptionI {
  value: string;
  label: string;
}

export interface MenuMultiChoicePropsI {
  options: MenuMultiChoiceOptionI[];
  value: string;
  onChange: (value: string) => void;
}

interface MenuItemChilderPropsI {
  children: React.ReactNode;
}

const Menu = (props: MenuPropsI): JSX.Element => {
  const classes = classNames({
    [styles.Menu]: true,
    [styles.Menu_horizontal]: props.orientation === 'horizontal',
    [styles.Menu_vertical]: props.orientation === 'vertical'
  });

  return (
    <ul
      {...props}
      className={classes}
      style={{
        justifyContent: props.justify && props.justify,
        height: props.height && props.height
      }}
    >
      {props.children}
    </ul>
  );
};

const MenuItem = ({
  onClick,
  variant,
  isactive,
  outsideLink,
  link,
  children,
  newWindow,
  hide
}: MenuItemPropsI) => {
  const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    onClick && onClick();
  };

  const classes = classNames({
    [styles.Menu__item]: true,
    [styles.Menu__item__light]: variant === 'light',
    [styles.Menu__item__light_active]: variant === 'light' && isactive,
    [styles.Menu__item__primary]: variant === 'primary',
    [styles.Menu__item__primary_active]: variant === 'primary' && isactive,
    [styles.Menu__item__default]: variant === 'default',
    [styles.Menu__item__dark]: variant === 'dark',
    [styles.Menu__item__dark_active]: variant === 'dark' && isactive,
    [styles.Menu__item__gray]: variant === 'gray',
    [styles.Menu__item__gray_active]: variant === 'gray' && isactive
  });

  if (hide) {
    return null;
  }

  return (
    <li onClick={handleClick} className={classes}>
      {link && !outsideLink && (
        <Link to={link} className={styles.Menu__item__link}>
          {children}
        </Link>
      )}
      {outsideLink && (
        <a href={link} className={styles.Menu__item__link} target={newWindow ? '_blank' : '_self'}>
          {children}
        </a>
      )}
      {!link && children}
    </li>
  );
};

const MenuItemLeft = (props: MenuItemChilderPropsI) => {
  return <div className={styles.Menu__item__left}>{props.children}</div>;
};

const MenuItemRight = (props: MenuItemChilderPropsI) => {
  return <div className={styles.Menu__item__right}>{props.children}</div>;
};

const MenuItemIcon = (props: MenuItemChilderPropsI) => {
  return <div className={styles.Menu__item__icon}>{props.children}</div>;
};

const MenuItemIconCircle = (props: MenuItemChilderPropsI) => {
  return <div className={styles.Menu__item__icon_circle}>{props.children}</div>;
};

const MenuItemSubText = (props: MenuItemChilderPropsI) => {
  return <div className={styles.Menu__item__subtext}>{props.children}</div>;
};

const MenuMultiChoice = ({ options, value, onChange }: MenuMultiChoicePropsI) => {
  return (
    <div className={styles.Menu__multichoice}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={classNames(styles.Menu__multichoice__item, {
            [styles.Menu__multichoice__item_active]: option.value === value
          })}
          onClick={(e) => {
            e.stopPropagation();
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

Menu.Item = MenuItem;
Menu.ItemLeft = MenuItemLeft;
Menu.ItemRight = MenuItemRight;
Menu.ItemIcon = MenuItemIcon;
Menu.ItemIconCircle = MenuItemIconCircle;
Menu.MultiChoice = MenuMultiChoice;

Menu.ItemSubText = MenuItemSubText;

export default Menu;
